import { semesterForDate } from '../../utils/semester';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './StudentResultsPage.css';
import { classroomLabel } from '../../utils/classroom';
const API = process.env.REACT_APP_API_URL || '/api';
const TOKEN = 'wadphol_student_token';
export default function StudentResultsPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN) || '');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [release, setRelease] = useState(null);
  const [profile, setProfile] = useState(null);
  const [year, setYear] = useState('');
  const [semester, setSemester] = useState('1');
  const [html, setHtml] = useState('');
  const [retestEligible, setRetestEligible] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const enrollment = profile?.enrollments?.find(row => String(row.academic_year_id) === year);
  const isSecondaryEnrollment = enrollment?.level_code?.startsWith('ม');
  const publishedSemester = Number(release?.published_semester ?? 1);
  const visibleSemester = !isSecondaryEnrollment || publishedSemester === 0 ? semester : String(publishedSemester);
  const generation = useRef(0);
  function logout() {
    generation.current += 1;
    sessionStorage.removeItem(TOKEN); setToken(''); setProfile(null); setHtml(''); setYear(''); setRetestEligible(false);
  }
  async function request(path, options = {}, authToken = token) {
    const res = await fetch(`${API}/student_portal/${path}`, { ...options,
      headers: { ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) }, cache: 'no-store' });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 && authToken) logout();
      if (res.status === 403) { setHtml(''); setRetestEligible(false); }
      throw new Error(data.message || 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่');
    }
    return res;
  }
  useEffect(() => {
    let cancelled = false;
    async function checkStatus() {
      try {
        const res = await fetch(`${API}/student_portal/status.php`, { cache: 'no-store' });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error('ไม่สามารถตรวจสอบสถานะระบบได้');
        if (!cancelled) {
          setRelease(data.data);
          if (!data.data.is_open) {
            generation.current += 1; setHtml(''); setRetestEligible(false);
          }
        }
      } catch (err) { if (!cancelled) setRelease({ is_open: false, message: 'ไม่สามารถตรวจสอบสถานะระบบได้ กรุณาลองใหม่อีกครั้ง' }); }
    }
    checkStatus();
    const timer = setInterval(checkStatus, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);
  useEffect(() => {
    if (!token || !release?.is_open) return;
    let cancelled = false;
    setError('');
    request('me.php').then(res => res.json()).then(data => {
      if (cancelled) return;
      setProfile(data); setYear(String(data.enrollments?.[0]?.academic_year_id || ''));
      if (data.enrollments?.[0]?.semester_settings) setSemester(String(semesterForDate(data.enrollments[0].semester_settings)));
    }).catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, release?.is_open]);
  useEffect(() => {
    const gen = ++generation.current;
    setHtml(''); setRetestEligible(false);
    if (!token || !year || !release?.is_open) return;
    const controller = new AbortController();
    setReportBusy(true); setError('');
    request(`report.php?academic_year_id=${encodeURIComponent(year)}&semester=${visibleSemester}`, { signal: controller.signal })
      .then(res => res.text()).then(async text => {
        if (gen !== generation.current) return;
        setHtml(text);
        const selected = profile?.enrollments?.find(row => String(row.academic_year_id) === year);
        if (selected?.level_code?.startsWith('ม')) {
          const res = await request(`retest.php?academic_year_id=${encodeURIComponent(year)}&semester=${visibleSemester}`, { signal: controller.signal });
          const data = await res.json();
          if (gen === generation.current) setRetestEligible(!!data.data?.length);
        }
      })
      .catch(err => { if (err.name !== 'AbortError' && gen === generation.current) setError(err.message); })
      .finally(() => { if (gen === generation.current) setReportBusy(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, year, visibleSemester, release?.is_open]);
  async function login(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const res = await request('login.php', { method: 'POST', body: JSON.stringify(credentials) }, '');
      const data = await res.json(); sessionStorage.setItem(TOKEN, data.token);
      setCredentials({ username: '', password: '' }); setToken(data.token);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function download(retest = false) {
    const gen = generation.current;
    setDownloadBusy(true); setError('');
    try {
      const res = await request(`${retest ? 'retest.php' : 'report.php'}?format=pdf&academic_year_id=${encodeURIComponent(year)}&semester=${visibleSemester}`);
      const blob = await res.blob();
      if (gen !== generation.current) return;
      const url = URL.createObjectURL(blob); const link = document.createElement('a');
      link.href = url; link.download = `${retest ? 'คำร้องสอบแก้ตัว' : 'ปพ6'}_${profile.student.student_code}_${year}_ภาค${visibleSemester}.pdf`;
      document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { if (gen === generation.current) setError(err.message); } finally { setDownloadBusy(false); }
  }

  return <main className="student-results">
    <header><div><span className="sr-eyebrow">ระบบวัดผลการศึกษา</span><h1>ผลการเรียนออนไลน์</h1><p>ดูผลการเรียนและดาวน์โหลด ปพ.6 ของตนเอง</p></div>
      {token && <button onClick={logout}>ออกจากระบบ</button>}</header>
    {error && <div className="sr-error" role="alert">{error}</div>}
    {!release ? <p role="status">กำลังตรวจสอบการเปิดระบบ…</p> : !release.is_open ? <section className="sr-login"><h2>การประกาศผลการเรียน</h2><p role="status">{release.message}</p><Link to="/login">เข้าสู่ระบบสำหรับเจ้าหน้าที่</Link></section> : !token ? <section className="sr-login"><h2>สำหรับนักเรียน</h2><form onSubmit={login}>
      <label>เลขประจำตัวนักเรียน<input required autoComplete="username" inputMode="numeric" value={credentials.username}
        onChange={e => setCredentials({ ...credentials, username: e.target.value })} /></label>
      <label>รหัสผ่าน<input required type="password" autoComplete="current-password" inputMode="numeric" maxLength={13}
        value={credentials.password} onChange={e => setCredentials({ ...credentials, password: e.target.value })} /></label>
      <button className="sr-primary" disabled={busy}>{busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบดูผลการเรียน'}</button>
    </form><Link to="/login">เข้าสู่ระบบสำหรับครูและเจ้าหน้าที่</Link></section> : !profile ? <p role="status">กำลังโหลดข้อมูลนักเรียน…</p> : <>
      <section className="sr-controls"><div><h2>{profile.student.prefix}{profile.student.firstname} {profile.student.lastname}</h2>
        <p>เลขประจำตัว {profile.student.student_code}{enrollment && ` · ${classroomLabel(enrollment)}`}</p></div>
        {profile.enrollments.length ? <div className="sr-filters"><label>ปีการศึกษา<select value={year} onChange={e => { const nextYear = e.target.value; setYear(nextYear); const row = profile.enrollments.find(item => String(item.academic_year_id) === nextYear); if (row?.semester_settings) setSemester(String(semesterForDate(row.semester_settings))); }}>
          {profile.enrollments.filter((row, i, all) => all.findIndex(r => r.academic_year_id === row.academic_year_id) === i).map(row => <option key={row.academic_year_id} value={row.academic_year_id}>{row.year}</option>)}</select></label>
          {isSecondaryEnrollment && <label>ภาคเรียน<select value={visibleSemester} onChange={e => setSemester(e.target.value)}>
            {(publishedSemester === 0 || publishedSemester === 1) && <option value="1">ภาคเรียนที่ 1</option>}
            {(publishedSemester === 0 || publishedSemester === 2) && <option value="2">ภาคเรียนที่ 2</option>}
          </select></label>}
          <button className="sr-primary" disabled={reportBusy || downloadBusy || !html} onClick={() => download()}>{downloadBusy ? 'กำลังดาวน์โหลด…' : 'ดาวน์โหลด ปพ.6 (PDF)'}</button>
          {retestEligible && <button disabled={reportBusy || downloadBusy} onClick={() => download(true)}>พิมพ์ใบคำร้องสอบแก้ตัว</button>}
        </div> : <p>ยังไม่มีข้อมูลห้องเรียน กรุณาติดต่อโรงเรียน</p>}</section>
      {reportBusy && <p role="status">กำลังโหลดผลการเรียน…</p>}
      {html && <div className="sr-report"><iframe title="ผลการเรียนรายบุคคล ปพ.6" sandbox="" srcDoc={html} /></div>}
    </>}
  </main>;
}
